<script lang="ts">
    import { onDestroy, setContext } from 'svelte'
    import { derived as sderived, writable, type Writable } from 'svelte/store'

    import * as yup from 'yup'

    import type { CrumbArray } from 'hydria/components/breadcrumb'
    import { validateSyncAt } from 'hydria/document_helpers'
    import { HydriaDocument, type IDocument } from 'hydria/HydriaDocument'
    import type { IRevision } from 'hydria/HydriaDocument'
    import { gettext as _, tgettext } from 'hydria/services/gettext'
    import { groups as ugroups } from 'hydria/services/session'
    import { spath } from 'hydria/svelte_helpers'

    import { Breadcrumb } from 'breadcrumb'

    import SimpleMarkdown from 'admin/field_components/SimpleMarkdown.svelte'
    import SimpleText from 'admin/field_components/SimpleText.svelte'
    import DocumentEdit from 'hydria/components/DocumentEdit.svelte'
    import LanguageSelector from 'hydria/components/Generics/LanguageSelector.svelte'
    import IntroHtml from 'hydria/field_components/IntroHtml.svelte'

    const unsubs: any[] = []
    onDestroy(() => {
        for (const unsub of unsubs) unsub.unsubscribe != null ? unsub.unsubscribe() : unsub()
    })

    interface Props {
        route: any
    }

    let { route }: Props = $props()
    const revFields = ['content_html', 'md_content_mdown', 'content_text', 'lg']

    interface ILocalDocument extends IDocument {
        block_name: string
        description?: string
        edit_fields: string[]
        required_fields: string[]
    }
    interface ILocalRevision extends IRevision {
        content_html?: string
        content_text?: string
        md_content_mdown?: string
    }

    class LocalDocument extends HydriaDocument {
        docType = 'block'
        docTypePlural = 'blocks'

        docTypeName = tgettext('Bloc de texte')
        docTypeNamePlural = tgettext('Blocs de texte')
    }

    const Document = new LocalDocument()
    const status = sderived(Document.status, ($status) => $status)

    let inDocId = $derived(route.result.path.params?.docId)
    let inRevId = $derived(route.result.path.params?.revId)

    const revSaved = sderived(Document.revision, ($rev) => (!$rev ? $rev : Object.fromEntries(Object.entries($rev).filter(([k, v]) => revFields.includes(k)))) as ILocalRevision)
    const docSaved = sderived(Document.document, ($rev) => ({ ...$rev }) as ILocalDocument)

    // revEdit is actually handled in DocumentEdit.
    let revEdit: Writable<ILocalRevision | undefined> = writable(undefined)

    setContext('revEdit', revEdit)
    setContext('Document', Document)
    setContext('attachments', Document.attachedFiles)

    let revShape: { [key: string]: yup.AnySchema } = $state({})
    let revSchema: any = $state(yup.object())

    function setupSchema() {
        revShape = {
            lg: yup
                .string()
                .required()
                .test('lgIsValid', '${path} is not valid', (value) => value === 'fr' || value === 'en'),
        }

        if ($docSaved.edit_fields.includes('content_html')) {
            revShape.content_html = yup
                .string()
                .test('contentHtmlIsValid', '${path} is not valid', (value) => {
                    if (value == null) return false
                    const clean = value.replaceAll(/<p><\/p>/gi, '')
                    return clean != ''
                })
                .required()
        }
        if ($docSaved.edit_fields.includes('md_content_mdown')) {
            revShape.md_content_mdown = yup.string().required()
        }
        if ($docSaved.edit_fields.includes('content_text')) {
            revShape.content_text = yup.string().required()
        }
        revSchema = yup.object().shape(revShape)
    }

    unsubs.push(
        status.subscribe(($status) => {
            // @ts-expect-error
            if ($revSaved && ['ready', 'saving', 'loaded'].includes($status)) {
                setupSchema()
            }
        }),
    )

    let formIsValid = $derived(revSchema.isValidSync($revEdit))

    const crumbs = sderived(status, ($status) => {
        const c: CrumbArray = [{ txt: Document.docTypeNamePlural, url: spath(`/${Document.docTypePlural.replace('_', '-')}`) }]
        switch ($status) {
            case 'loading':
                c.push({
                    txt: '…',
                })
                break
            case 'error':
            case 'not-found':
                c.push({
                    icon: 'mdi:alert',
                })
                break
            case 'ready':
                c.push({
                    txt: tgettext('Edit'),
                })
                c.push({
                    txt: `<span class="text-muted">${Document.docId}</span>`,
                    html: true,
                })
        }
        return c
    })
</script>

<Breadcrumb {crumbs} />

<DocumentEdit {Document} docId={inDocId} revId={inRevId} {formIsValid} {revFields} canUnpublish={$ugroups.has('dev')}>
    {#if $revEdit}
        <div class="card card-content">
            <div class="card-header text-bg-primary">{$_('Document')}</div>
            <div class="card-body d-grid gap-2">
                <div class="row g-3 align-items-baseline">
                    <div class="col-2 text-end">
                        <span class="control-label">ID</span>
                    </div>
                    <div class="col">
                        <tt>{$docSaved.block_name}</tt>
                    </div>
                </div>

                <div class="row g-3 align-items-baseline">
                    <div class="col-2 text-end">
                        <span class="control-label">Description</span>
                    </div>
                    <div class="col">
                        {#if $docSaved.description}{$docSaved.description}
                        {:else}<em class="text-muted">{$_('non-configuré')}</em>
                        {/if}
                    </div>
                </div>
                <LanguageSelector bind:value={$revEdit.lg} />

                {#if $docSaved.edit_fields.includes('content_html')}
                    <IntroHtml
                        saved={$revSaved.content_html}
                        bind:value={$revEdit.content_html}
                        disabled={$status != 'ready'}
                        label={tgettext('Contenu (HTML)')}
                        helpText={undefined}
                        isValid={(v) => {
                            return validateSyncAt(revSchema, 'content_html', { content_html: v })
                        }}
                    />
                {/if}

                {#if $docSaved.edit_fields.includes('md_content_mdown')}
                    <SimpleMarkdown saved={$revSaved.md_content_mdown} bind:value={$revEdit.md_content_mdown} disabled={$status != 'ready'} label={tgettext('Contenu (Markdown)')} helpText={undefined} isValid={(v) => validateSyncAt(revSchema, 'md_content_mdown', { md_content_mdown: v })} />
                {/if}

                {#if $docSaved.edit_fields.includes('content_text')}
                    <SimpleText saved={$revSaved.content_text} bind:value={$revEdit.content_text} disabled={$status != 'ready'} label={tgettext('Contenu (Markdown)')} helpText={undefined} isValid={(v) => validateSyncAt(revSchema, 'content_text', { content_text: v })} />
                {/if}
            </div>
        </div>
    {/if}
</DocumentEdit>
